import { Icon, Tooltip } from '@blueprintjs/core';
import BOSSelect from 'Components/Generic/BOSSelect';
import {
  showSaveSuccessToast,
  showToast,
} from 'Components/Generic/BOSToasterUtil';
import DeleteButton from 'Components/Generic/Buttons/DeleteButton';
import EditableDetailPanel from 'Components/Generic/DetailPanel/EditableDetailPanel';
import DropOrChooseFilePicker from 'Components/Generic/Documents/DropOrChooseFilePicker';
import {
  DOCUMENT_TYPE,
  getDocumentType,
} from 'Components/Generic/Documents/Enum';
import BOSNoWrapText from 'Components/Generic/Misc/BOSNoWrapText';
import NoContentBox from 'Components/Generic/Misc/NoContentBox';
import { useAlertContext } from 'Data/context/Alerts';
import DocumentData from 'Data/DocumentData';
import { user_roles } from 'Data/Enum';
import DataManager from 'Lib/DataManager';
import Eventer from 'Lib/Eventer';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import { areEquivalent, isUserAuthorized } from 'Util';
import { formatDate } from 'Util/dateUtil';
import styles from './DocumentListPanel.module.css';

let DM = new DataManager();

const DocumentListPanel = ({
  documentType = '',
  documents,
  associatedDocuments,
  editable = false,
  canAddDoc = true,
  uploadDocFunc = DocumentData.uploadDocument,
  deleteDocFunc = () => {},
  onDocumentsUpdated = () => {},
  docJoinTableData,
  hideTypeColumn = false,
  returnTableOnly = false,
  associatedDocumentLabel = 'Associated Documents',
  ...props
}) => {
  const DocListDataBusRef = useRef(Eventer.createBus());
  let DocListDataBus = DocListDataBusRef.current;

  const { openDeleteDialog, showDeletedToast } = useAlertContext();

  const [documentList, setDocumentList] = useState([]);
  const [associatedDocumentList, setAssociatedDocumentList] = useState([]);

  const [documentsToUpload, setDocumentsToUpload] = useState([]);
  const [refreshNewDocsKey, setRefreshNewDocsKey] = useState(0);

  useEffect(() => {
    DM.add(
      DocumentData.registerListener(
        'DOCUMENT_UPLOAD_ERROR',
        onDocumentUploadError,
      ),
      DocumentData,
    );
    DM.add(
      DocListDataBus.registerListener('DOCS_UPLOADED', onDocumentsUploaded),
      DocListDataBus,
    );
    DM.add(
      DocumentData.registerListener('RECORD_UPDATED', onDocumentUpdated),
      DocumentData,
    );
    if (docJoinTableData) {
      DM.add(
        docJoinTableData.registerListener('RECORDS_DELETED', onDocumentDeleted),
        docJoinTableData,
      );
    }
    return () => {
      DM.clear();
    };
  }, []);

  useEffect(() => {
    setDocumentList(documents || []);
    setRefreshNewDocsKey((prevState) => prevState + 1);
  }, [documents]);

  useEffect(() => {
    let assocDoc = associatedDocuments || [];
    setAssociatedDocumentList(
      assocDoc.filter((d) => !documentList.find((doc) => d.id === doc.id)),
    );
  }, [associatedDocuments, documentList]);

  const onDocumentUpdated = (payload) => {
    if (payload) {
      showSaveSuccessToast('Document type updated');
      onDocumentsUpdated();
    }
  };

  const onDocumentsUploaded = (payload) => {
    let successfulUploads =
      payload?.uploadResults?.filter((res) => res.status === 'fulfilled')
        .length || 0;

    successfulUploads > 0 &&
      showSaveSuccessToast(
        `${successfulUploads} document${
          successfulUploads?.length > 0 ? 's' : ''
        } uploaded to ${documentType.toLowerCase()}`,
      );
    onDocumentsUpdated();
  };

  const deleteClicked = (doc) => {
    openDeleteDialog({
      objectType: 'Document',
      onConfirmDelete: () => deleteDocFunc(doc),
    });
  };

  const onDocumentDeleted = () => {
    showDeletedToast('Document');
    onDocumentsUpdated();
  };

  const onDocumentUploadError = () => {
    showToast('Error uploading document(s)', 'danger', 'warning-sign');
    onDocumentsUpdated();
  };

  const updateItemValue = (itemToUpdate, updateField, updatedValue) => {
    if (itemToUpdate?.id) {
      const updatedArray = documentList.map((item) => {
        if (item === itemToUpdate) {
          let originalItem = { ...item };
          let updatedItem = { ...item, [updateField]: updatedValue };
          if (itemToUpdate?.id && !areEquivalent(originalItem, updatedItem)) {
            DocumentData.updateRecord(updatedItem);
          }
          return updatedItem;
        }
        return item;
      });
      setDocumentList(updatedArray);
    }
  };

  const getTypeDropdownForDoc = (doc) => {
    return (
      <BOSSelect
        defaultSelectedId={doc?.type}
        onItemSelect={(type) => {
          doc && updateItemValue(doc, 'type', type?.key || null);
        }}
        listData={Object.entries(DOCUMENT_TYPE).map(([key, value]) => {
          return {
            key,
            value,
          };
        })}
        idField={'key'}
        labelField={'value'}
      />
    );
  };

  const getHeaders = (inEditState) => {
    return (
      <tr>
        <td className={styles.table_header_cell}>Name</td>
        {!hideTypeColumn && (
          <td className={`${styles.table_header_cell} ${styles.centered}`}>
            Type
          </td>
        )}
        <td className={`${styles.table_header_cell} ${styles.centered}`}>
          Uploaded
        </td>
        {inEditState && <td />}
      </tr>
    );
  };

  const getDocRow = (doc, key, editable = false) => {
    return (
      <tr key={key}>
        <td>
          <Tooltip content='Open file' openOnTargetFocus={false}>
            <a
              href={`filer/${doc.id}/${doc.original_name}`}
              target={'_blank'}
              rel='noreferrer'>
              <BOSNoWrapText maxWidth={300}>{doc.original_name}</BOSNoWrapText>
            </a>
          </Tooltip>
        </td>
        {!hideTypeColumn && (
          <td className={styles.centered}>
            {editable ? getTypeDropdownForDoc(doc) : getDocumentType(doc.type)}
          </td>
        )}
        <td className={styles.centered}>{formatDate(doc.createdAt)}</td>
        <td>
          <div className={styles.button_group}>
            <Tooltip content='Download' openOnTargetFocus={false}>
              <a
                className={'bp5-button bp5-minimal bp5-intent-primary'}
                style={{ padding: 4 }}
                href={`filer/${doc.id}`}
                download={doc.original_name}>
                <Icon icon={'import'} />
              </a>
            </Tooltip>
            {editable &&
              isUserAuthorized({
                roles: [
                  user_roles.ADMINISTRATOR,
                  user_roles.BUYER,
                  user_roles.TRANSACTOR,
                ],
              }) && (
                <DeleteButton
                  miniVersion
                  tooltipText={'Delete File'}
                  onDeleteItem={() => deleteClicked(doc)}
                />
              )}
          </div>
        </td>
      </tr>
    );
  };

  const getDocumentListContent = (inEditState) => {
    return !documentList || documentList.length === 0 ? (
      <tr>
        <td colSpan={!hideTypeColumn ? 4 : 3}>
          <NoContentBox label={'No Files'} />
        </td>
      </tr>
    ) : (
      documentList.map((doc, index) => getDocRow(doc, index, inEditState))
    );
  };

  const getAssociatedDocumentListContent = () => {
    return (
      associatedDocumentList?.length > 0 &&
      associatedDocumentList.map((doc, index) => getDocRow(doc, `ad-${index}`))
    );
  };

  const handleFilesUpload = async () => {
    let docUploads = documentsToUpload.map(async (docToUpload) => {
      return await uploadDocFunc(docToUpload);
    });

    Promise.allSettled(docUploads).then((promiseResults) => {
      DocListDataBus.notifyListeners('DOCS_UPLOADED', {
        documentsToUpload,
        uploadResults: promiseResults,
      });
    });
  };

  const uploadButton = documentsToUpload?.length > 0 && (
    <button className={styles.upload_button} onClick={handleFilesUpload}>
      <Icon icon={'upload'} />
      {`Upload File${documentsToUpload?.length > 1 ? 's' : ''}`}
    </button>
  );

  let newFileInputComponent = canAddDoc && (
    <div className={styles.file_input_container}>
      <DropOrChooseFilePicker
        key={refreshNewDocsKey}
        onFilesSelected={setDocumentsToUpload}
      />
      <div className={styles.upload_button_container}>{uploadButton}</div>
    </div>
  );

  const getDocTable = (inEditState) => {
    return (
      <>
        {(!documentList || documentList.length === 0) &&
        (!associatedDocumentList || associatedDocumentList.length === 0) ? (
          !canAddDoc && <NoContentBox label={'No Files'} />
        ) : (
          <table
            className={
              returnTableOnly
                ? styles.document_table_condensed
                : styles.document_table
            }>
            <thead>{getHeaders(inEditState)}</thead>
            <tbody>{getDocumentListContent(inEditState)}</tbody>
            {associatedDocumentList?.length > 0 && (
              <tfoot>
                <tr>
                  <td colSpan={4} className={styles.associated_docs_header}>
                    {associatedDocumentLabel}
                  </td>
                </tr>
                {getAssociatedDocumentListContent()}
              </tfoot>
            )}
          </table>
        )}
        {newFileInputComponent}
      </>
    );
  };

  return returnTableOnly ? (
    <>{getDocTable(true)}</>
  ) : (
    <EditableDetailPanel
      title={`${documentType} Documents`}
      editable={editable}
      renderChild={getDocTable}
      {...props}
    />
  );
};

DocumentListPanel.propTypes = {
  documentType: PropTypes.string,
  documents: PropTypes.array,
  associatedDocuments: PropTypes.array,
  editable: PropTypes.bool,
  canAddDoc: PropTypes.bool,
  uploadDocFunc: PropTypes.func,
  deleteDocFunc: PropTypes.func,
  onDocumentsUpdated: PropTypes.func,
  docJoinTableData: PropTypes.object,
  hideTypeColumn: PropTypes.bool,
  returnTableOnly: PropTypes.bool,
  associatedDocumentLabel: PropTypes.string,
};

export default DocumentListPanel;
